import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { api } from '../../config/api';

// Fallback tiles shown until real videos are uploaded via admin.
// Poster-only (no fake video) so nothing ever plays broken footage.
// Upload real .mp4 URLs in the admin Gallery for the blinq-style autoplay effect.
const fallbackVideos = [
  { title: 'Steak on the Grill', category: 'Grill', image_url: 'https://images.unsplash.com/photo-1544025162-d76694265947?w=1200&q=80', video_url: '' },
  { title: 'Chef in Action', category: 'Kitchen', image_url: 'https://images.unsplash.com/photo-1466637574441-749b8f19452f?w=1200&q=80', video_url: '' },
  { title: 'Fresh Juice Pour', category: 'Drinks', image_url: 'https://images.unsplash.com/photo-1600271886742-f049cd451bba?w=1200&q=80', video_url: '' },
];

const isMp4 = (url) => /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url || '');

const toYouTubeEmbed = (url) => {
  if (!url) return null;
  const yt = (url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/) || [])[1];
  return yt ? yt : null;
};

const FoodVideos = () => {
  const [videos, setVideos] = useState([]);
  const [muted, setMuted] = useState(true); // global mute; autoplay requires muted to start
  const tileRefs = useRef([]);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await api.get('/gallery.php');
        if (res.data.success) {
          const vids = res.data.data.filter(v => (v.media_type || 'image') === 'video');
          setVideos(vids.length > 0 ? vids.slice(0, 4) : fallbackVideos);
          return;
        }
      } catch (e) { /* use fallback */ }
      setVideos(fallbackVideos);
    };
    fetchVideos();
  }, []);

  // blinq-style: autoplay muted <video> when it scrolls into view, pause when out
  useEffect(() => {
    if (videos.length === 0) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const vid = entry.target.querySelector('video');
        if (!vid) return;
        if (entry.isIntersecting) vid.play().catch(() => {});
        else vid.pause();
      });
    }, { threshold: 0.35 });

    tileRefs.current.forEach(el => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [videos]);

  // Keep all <video> elements' muted state in sync with the toggle
  useEffect(() => {
    tileRefs.current.forEach(el => {
      const vid = el && el.querySelector('video');
      if (vid) vid.muted = muted;
    });
  }, [muted, videos]);

  if (videos.length === 0) return null;

  const hasSound = videos.some(v => isMp4(v.video_url));

  return (
    <section className="section-padding" style={{ backgroundColor: '#1A1A1A' }}>
      <div className="container">
        <div className="text-center mb-5">
          <span className="section-subtitle" style={{ color: '#D4AF37' }}>Taste in Motion</span>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '2.5rem', fontWeight: 600, color: '#fff' }}>
            Watch Us Cook
          </h2>
          <div className="gold-divider-center" style={{ background: '#D4AF37' }}></div>
          <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.6)', marginTop: '12px' }}>
            A feast for the eyes — see the sizzle, the flame, and the craft behind every plate.
          </p>
          {hasSound && (
            <button
              onClick={() => setMuted(m => !m)}
              style={{
                marginTop: '10px', display: 'inline-flex', alignItems: 'center', gap: '8px',
                background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.4)',
                color: '#D4AF37', borderRadius: '30px', padding: '8px 18px', cursor: 'pointer',
                fontSize: '0.8rem', fontWeight: 600
              }}
            >
              {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
              {muted ? 'Tap to Unmute' : 'Mute'}
            </button>
          )}
        </div>

        <div className="row g-3 justify-content-center">
          {videos.map((v, idx) => {
            const poster = v.image_url || v.poster;
            const ytId = toYouTubeEmbed(v.video_url);
            const mp4 = isMp4(v.video_url);
            return (
              <div key={idx} className="col-6 col-md-4 col-lg-3">
                <div
                  ref={el => tileRefs.current[idx] = el}
                  className="hover-lift"
                  style={{
                    borderRadius: '16px', overflow: 'hidden', position: 'relative',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
                    aspectRatio: '9 / 16', backgroundColor: '#000'
                  }}
                >
                  {mp4 ? (
                    <video
                      src={v.video_url}
                      poster={poster}
                      autoPlay muted loop playsInline preload="metadata"
                      style={{ height: '100%', width: '100%', objectFit: 'cover', display: 'block', background: '#000' }}
                    />
                  ) : ytId ? (
                    <iframe
                      title={v.title}
                      src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&loop=1&playlist=${ytId}&controls=0&modestbranding=1&playsinline=1&rel=0&enablejsapi=1&iv_load_policy=3&fs=0`}
                      style={{ height: '100%', width: '100%', border: 0, display: 'block', pointerEvents: 'none' }}
                      allow="autoplay; encrypted-media; picture-in-picture"
                      tabIndex={-1}
                    />
                  ) : (
                    // No playable video yet — show poster only (no broken player)
                    <img
                      src={poster}
                      alt={v.title}
                      style={{ height: '100%', width: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  )}

                  {/* Bottom caption gradient */}
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(transparent 55%, rgba(0,0,0,0.85))',
                    display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                    padding: '18px', pointerEvents: 'none'
                  }}>
                    <h5 style={{ fontFamily: 'Playfair Display, serif', color: '#fff', fontSize: '1rem', marginBottom: '2px' }}>
                      {v.title}
                    </h5>
                    <span style={{ color: '#D4AF37', fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase' }}>
                      {v.category || 'Video'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FoodVideos;
